import React, { useState, useEffect, useRef } from 'react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import {
  ShopierService,
  ShopierCheckoutResponse,
  OrderStatusResponse,
} from '../../services/shopierService';
import { SUBSCRIPTION_PLANS } from '../../config/constants';
import { legalConfig } from '../../config/legalConfig';
import { normalizeQuota } from '../../services/quotaNormalization';
import confetti from 'canvas-confetti';
import {
  X,
  Lock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Receipt,
  Building2,
  Ship,
  ExternalLink,
  RefreshCw,
  Info,
} from 'lucide-react';

interface ShopierModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetPlanId?: string;
  selectedPlanId?: string;
  planName?: string;
  amountTRY?: number;
  billingPeriod?: string;
  siteId?: string;
  isRenewal?: boolean;
  targetSubscriptionId?: string;
  onSuccess?: () => void;
}

export const ShopierModal: React.FC<ShopierModalProps> = ({
  isOpen,
  onClose,
  targetPlanId,
  selectedPlanId: propSelectedPlanId,
  planName: _planName,
  amountTRY: _amountTRY,
  billingPeriod: _billingPeriod,
  siteId: _siteId,
  isRenewal,
  targetSubscriptionId,
  onSuccess,
}) => {
  const { activeSite, updateSiteData, refreshSectorQuotas } = useSite();
  const { currentUser } = useAuth();

  const selectedPlanId =
    propSelectedPlanId ||
    targetPlanId ||
    (activeSite?.industry === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL');
  const plan = SUBSCRIPTION_PLANS[selectedPlanId] || SUBSCRIPTION_PLANS['PLAN_CORP_ANNUAL'];

  const [step, setStep] = useState<'FORM' | 'AWAITING_PAYMENT' | 'UNCONFIGURED' | 'SUCCESS'>('FORM');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Customer Contact Fields (Real billing customer info)
  const [customerName, setCustomerName] = useState(currentUser?.displayName || 'Ahmet Yılmaz');
  const [customerEmail, setCustomerEmail] = useState(currentUser?.email || 'musteri@adiosoft.site');
  const [customerPhone, setCustomerPhone] = useState('+90 532 000 0000');
  const [billingAddress, setBillingAddress] = useState('Maslak Mah. Büyükdere Cad. No: 123 Sarıyer / İstanbul');

  // Mandatory Legal Acceptance States
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptDistanceSales, setAcceptDistanceSales] = useState(false);
  const [acceptImmediateExecution, setAcceptImmediateExecution] = useState(false);

  // Checkout Result
  const [checkoutData, setCheckoutData] = useState<ShopierCheckoutResponse | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const pollingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      pollingRef.current = false;
      setStep('FORM');
      setLoading(false);
      setErrorMessage(null);
      setCheckoutData(null);
      setAcceptTerms(false);
      setAcceptDistanceSales(false);
      setAcceptImmediateExecution(false);
    }
  }, [isOpen]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      pollingRef.current = false;
    };
  }, []);

  if (!isOpen) return null;

  const handleStartCheckout = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptTerms || !acceptDistanceSales || !acceptImmediateExecution) {
      setErrorMessage('Devam edebilmek için lütfen Kullanım Koşulları, Ön Bilgilendirme ve cayma hakkı istisnasına ilişkin onay kutularını işaretleyiniz.');
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const idempotencyKey = `checkout_${currentUser?.uid || 'guest'}_${plan.id}_${Date.now()}`;

      const res = await ShopierService.createCheckout({
        siteId: activeSite?.id,
        userId: currentUser?.uid || 'guest_user',
        planId: plan.id,
        isRenewal: Boolean(isRenewal),
        targetSubscriptionId,
        customerName,
        customerEmail,
        customerPhone,
        billingAddress,
        idempotencyKey,
        termsVersion: legalConfig.termsVersion,
        distanceSalesVersion: legalConfig.distanceSalesVersion,
        legalAccepted: true,
        immediateExecutionRequested: true,
      });

      setCheckoutData(res);
      setOrderId(res.orderId || null);

      if (!res.success) {
        if (!res.configured && !res.error) {
          setStep('UNCONFIGURED');
        } else {
          setErrorMessage(res.error || 'Ödeme oturumu oluşturulamadı.');
        }
        setLoading(false);
        return;
      }

      if (res.checkoutUrl || res.checkoutHtml) {
        setStep('AWAITING_PAYMENT');
        setLoading(false);

        // Execute Shopier checkout (safe top redirect or auto-submitting form)
        ShopierService.executeCheckout(res);

        // Start background polling for order status
        if (res.orderId) {
          startPolling(res.orderId);
        }
      } else {
        setErrorMessage(res.error || 'Ödeme bağlantısı alınamadı.');
        setLoading(false);
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Beklenmeyen bir hata oluştu.');
      setLoading(false);
    }
  };

  const startPolling = async (targetOrderId: string) => {
    pollingRef.current = true;
    const isPaid = await ShopierService.pollOrderStatus(
      targetOrderId,
      (statusRes) => {
        if (!pollingRef.current) return;
        if (statusRes.isPaid) {
          handlePaymentSuccess(statusRes);
        }
      },
      45, // 45 attempts * 2.5s = ~110 seconds
      2500
    );

    if (isPaid && pollingRef.current) {
      // Completed via polling
    }
  };

  const handleManualCheck = async () => {
    if (!orderId) return;
    setCheckingStatus(true);
    try {
      const statusRes = await ShopierService.getOrderStatus(orderId);
      if (statusRes.isPaid) {
        handlePaymentSuccess(statusRes);
      } else {
        setErrorMessage('Ödemeniz henüz Shopier tarafından onaylanmadı. Lütfen ödemeyi tamamladıktan sonra tekrar deneyin.');
      }
    } catch {
      setErrorMessage('Sipariş durumu kontrol edilemedi.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handlePaymentSuccess = async (_statusRes?: OrderStatusResponse) => {
    pollingRef.current = false;
    setStep('SUCCESS');

    // Authoritative quota and subscription refresh from server/database
    await refreshSectorQuotas();

    if (activeSite) {
      const normLimit = normalizeQuota(plan.websiteLimit);
      const effectiveLimit = normLimit.valid ? normLimit.value : (plan.websiteLimit || 0);

      updateSiteData({
        subscription: {
          planId: plan.id,
          planName: plan.name,
          priceTRY: plan.priceTRY,
          startDate: Date.now(),
          expireDate: Date.now() + 365 * 24 * 60 * 60 * 1000,
          status: 'ACTIVE',
          lastPaymentId: orderId || 'SHP_SUCCESS',
          websiteLimit: effectiveLimit,
        },
      });
    }

    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
    });

    if (onSuccess) {
      try {
        onSuccess();
      } catch (err) {
        console.error('Error invoking onSuccess:', err);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in select-none">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[92vh]">
        {/* Shopier Branded Header */}
        <div className="p-4 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-md flex items-center justify-center font-extrabold text-sm tracking-tight text-white shadow-inner">
              S
            </div>
            <div>
              <div className="flex items-center gap-1.5 font-bold text-sm">
                <span>Shopier Güvenli Ödeme Portalı</span>
                <Lock className="w-3.5 h-3.5 opacity-80" />
              </div>
              <div className="text-[10px] opacity-90">256-Bit SSL Şifreli Resmi Ödeme Altyapısı</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Order Summary Bar */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
          <div>
            <div className="flex items-center gap-1.5 font-bold text-slate-800">
              {plan.industry === 'YACHT_RENTAL' ? (
                <Ship className="w-3.5 h-3.5 text-blue-600" />
              ) : (
                <Building2 className="w-3.5 h-3.5 text-blue-600" />
              )}
              <span>{plan.name}</span>
            </div>
            <span className="text-[11px] text-slate-500">
              {activeSite?.name || 'Sektörel Paket'} — 1 Yıllık Lisans
            </span>
          </div>
          <div className="text-right">
            <span className="text-base font-extrabold text-slate-900">
              {plan.priceTRY.toLocaleString('tr-TR')} TL
            </span>
            <span className="text-[10px] text-slate-400 block">KDV Dahil / Yıllık</span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-4">
          {errorMessage && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
              <div>{errorMessage}</div>
            </div>
          )}

          {/* STEP 1: FORM */}
          {step === 'FORM' && (
            <form onSubmit={handleStartCheckout} className="space-y-4 text-xs text-slate-700">
              <div className="p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-900 text-xs flex items-start gap-2.5">
                <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="leading-relaxed">
                  Ödemeniz <strong>Shopier güvencesiyle</strong> doğrudan Shopier SSL korumalı ödeme sayfasında gerçekleştirilecektir. Kredi kartı bilgileriniz sunucularımızda saklanmaz.
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Ad Soyad / Fatura Ünvanı
                </label>
                <input
                  required
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Örn: Ahmet Yılmaz"
                  className="w-full px-3 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">E-Posta Adresi</label>
                  <input
                    required
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="ornek@sirket.com"
                    className="w-full px-3 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-medium"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Telefon Numarası</label>
                  <input
                    required
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+90 532 000 0000"
                    className="w-full px-3 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Fatura Adresi</label>
                <textarea
                  rows={2}
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  placeholder="Fatura kesilecek adres bilgisi..."
                  className="w-full px-3 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-medium resize-none"
                />
              </div>

              {/* Pre-Checkout Disclosures & Turkish Consumer Law Compliance */}
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5 text-slate-600 text-[11px] leading-relaxed">
                <div className="font-semibold text-slate-800 text-xs flex items-center justify-between">
                  <span>Ön Bilgilendirme ve Lisans Kapsamı</span>
                  <span className="text-amber-600">Süreli Dijital Lisans</span>
                </div>
                <p>
                  Satın alınan dijital paket, seçilen abonelik dönemi (1 Yıl) boyunca geçerli süreli bir kullanım hakkıdır. Kaynak kodlarının veya yazılım mülkiyetinin devri niteliği taşımaz. Toplam tutara tüm vergiler (KDV) dahildir.
                </p>

                <div className="space-y-2 pt-1 border-t border-slate-200">
                  {/* Checkbox 1: Terms */}
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-0.5 rounded text-amber-600 focus:ring-amber-500"
                    />
                    <span>
                      <a
                        href="/kullanim-kosullari"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline font-medium hover:text-blue-700"
                      >
                        Kullanım Koşulları
                      </a>
                      &apos;nı okudum ve kabul ediyorum.
                    </span>
                  </label>

                  {/* Checkbox 2: Distance Sales & Pre-info */}
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={acceptDistanceSales}
                      onChange={(e) => setAcceptDistanceSales(e.target.checked)}
                      className="mt-0.5 rounded text-amber-600 focus:ring-amber-500"
                    />
                    <span>
                      <a
                        href="/mesafeli-satis-ve-on-bilgilendirme"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline font-medium hover:text-blue-700"
                      >
                        Ön Bilgilendirme Formu ve Mesafeli Hizmet Sözleşmesi
                      </a>
                      &apos;ni okudum, onaylıyorum.
                    </span>
                  </label>

                  {/* Checkbox 3: Immediate Execution & Right of Withdrawal Exception */}
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={acceptImmediateExecution}
                      onChange={(e) => setAcceptImmediateExecution(e.target.checked)}
                      className="mt-0.5 rounded text-amber-600 focus:ring-amber-500"
                    />
                    <span>
                      Hizmetin elektronik ortamda derhal başlatılmasını talep ettiğimi ve Mesafeli Sözleşmeler Yönetmeliği m. 15/1-ğ uyarınca anında ifa edilen dijital hizmetler kapsamında cayma hakkımın istisna kapsamına girebileceği konusunda bilgilendirildiğimi kabul ediyorum.
                    </span>
                  </label>
                </div>
              </div>

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold rounded-xl shadow-lg shadow-orange-500/25 transition flex items-center justify-center gap-2 text-xs sm:text-sm disabled:opacity-50 active:scale-[0.99]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Shopier Ödeme Oturumu Başlatılıyor...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      <span>Ödeme Yap ve Hizmeti Başlat ({plan.priceTRY.toLocaleString('tr-TR')} TL)</span>
                    </>
                  )}
                </button>
                <span className="text-[10px] text-slate-400 block text-center mt-1.5 font-medium">
                  Bu butona tıklayarak ödeme yükümlülüğü doğuran siparişi onaylamış olursunuz.
                </span>
              </div>

              <div className="flex items-center justify-center gap-4 text-[11px] text-slate-400 pt-1">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                  256-Bit SSL Koruma
                </span>
                <span>•</span>
                <span>12 Aya Varan Taksit</span>
                <span>•</span>
                <span>Anında Aktivasyon</span>
              </div>
            </form>
          )}

          {/* STEP 2: AWAITING PAYMENT (REAL SHOPIER CHECKOUT) */}
          {step === 'AWAITING_PAYMENT' && (
            <div className="space-y-4 text-center py-2">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center mx-auto animate-pulse">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>

              <div className="space-y-1">
                <h3 className="font-bold text-base text-slate-900">Shopier Ödemeniz Bekleniyor</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Açılan yeni sekmede Shopier ödeme işleminizi tamamlayınız. Ödeme onaylandığında aboneliğiniz sistem tarafından otomatik aktifleştirilecektir.
                </p>
              </div>

              {orderId && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600 flex items-center justify-between">
                  <span className="font-medium text-slate-500">Sipariş No:</span>
                  <span className="font-mono font-bold text-slate-800">{orderId}</span>
                </div>
              )}

              {(checkoutData?.checkoutUrl || checkoutData?.checkoutHtml) && (
                <button
                  type="button"
                  onClick={() => {
                    if (checkoutData?.checkoutUrl) {
                      window.open(checkoutData.checkoutUrl, '_blank', 'noopener,noreferrer');
                    } else if (checkoutData) {
                      ShopierService.executeCheckout(checkoutData);
                    }
                  }}
                  className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 text-xs"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Ödeme Sayfasını Tekrar Aç</span>
                </button>
              )}

              <div className="pt-2 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleManualCheck}
                  disabled={checkingStatus}
                  className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl text-xs transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${checkingStatus ? 'animate-spin' : ''}`} />
                  <span>{checkingStatus ? 'Kontrol Ediliyor...' : 'Ödemeyi Kontrol Et'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setStep('FORM')}
                  className="text-xs text-slate-400 hover:text-slate-600 transition"
                >
                  Bilgileri Değiştir veya Geri Dön
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: UNCONFIGURED (EXPLAINING SHOPIER_PAT CREDENTIAL SETUP) */}
          {step === 'UNCONFIGURED' && (
            <div className="space-y-4 text-left py-2">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold text-sm text-amber-800">
                  <Info className="w-4 h-4 text-amber-600" />
                  <span>Shopier Entegrasyonu Hazır</span>
                </div>
                <p className="leading-relaxed">
                  Adiosoft gerçek Shopier ödeme altyapısı (SIPARIS, WEBHOOK, ENTITLEMENT) kodlanmıştır.
                  Canlı ödemeleri alabilmek için <strong>Google AI Studio Secrets</strong> panelinde:
                </p>
                <div className="p-2.5 bg-amber-100/70 rounded-lg font-mono font-bold text-amber-950 text-center border border-amber-300">
                  SHOPIER_PAT
                </div>
                <p className="text-[11px] text-amber-800 leading-relaxed">
                  değişkeni tanımlandığında sistem anında canlı ödeme oturumu oluşturacaktır.
                </p>
              </div>

              {orderId && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs flex items-center justify-between">
                  <span className="text-slate-500 font-medium">Oluşturulan Sipariş ID:</span>
                  <span className="font-mono font-bold text-slate-800">{orderId}</span>
                </div>
              )}

              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs transition"
              >
                Anladım, Kapat
              </button>
            </div>
          )}

          {/* STEP 4: SUCCESS */}
          {step === 'SUCCESS' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <div className="space-y-1">
                <h3 className="text-lg font-extrabold text-slate-900">Ödeme Başarıyla Tamamlandı!</h3>
                <p className="text-xs text-slate-500">
                  Aboneliğiniz ve site haklarınız veritabanında başarıyla güncellendi.
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-left space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Paket:</span>
                  <span className="font-bold text-slate-800">{plan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tutar:</span>
                  <span className="font-bold text-slate-800">
                    {plan.priceTRY.toLocaleString('tr-TR')} TL
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Süre:</span>
                  <span className="font-bold text-slate-800">1 Yıl (365 Gün)</span>
                </div>
                {orderId && (
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="text-slate-500">Sipariş No:</span>
                    <span className="font-mono font-bold text-slate-800">{orderId}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition shadow-lg shadow-emerald-600/20"
              >
                Yönetim Paneline Dön
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
